import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SESSION_END_CODES, type ConflictingSession, type SessionConflictNotice, type SessionState } from '@pqrs/contracts';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogMedia, AlertDialogTitle } from '@xiltrion/ui/alert-dialog';
import { Button } from '@xiltrion/ui/button';
import { Icono } from '@xiltrion/ui/icono';
import { Spinner } from '@xiltrion/ui/spinner';
import { avisar } from '@xiltrion/ui/sonner';
import { apiGet, apiPost, ApiError, onSessionConflict } from '@/api/client';
import type { SessionEndReason } from './activity';

/** Con la pestaña abierta se pregunta seguido (es la señal de vida de la sesión); en espera, más aún. */
const POLL_MS = 10_000;
const POLL_WAITING_MS = 2_000;
const END_CODES = new Set<string>(Object.values(SESSION_END_CODES));

const NOTICE: Record<SessionConflictNotice, string> = {
  CHOSEN_ELSEWHERE: 'Elegiste continuar en esta sesión desde el otro equipo. La otra se cerró.',
  OTHER_GONE: 'La otra sesión se cerró o dejó de responder. Continúas en esta.',
  TIMEOUT_KEPT: 'No se eligió a tiempo: sigue solo esta sesión, la del ingreso más reciente.',
};

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.max(0, s % 60)).padStart(2, '0')}`;
const hhmm = (iso: string) => new Date(iso).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });

/** «Chrome en Windows»: lo justo para reconocer el otro equipo o navegador. */
function describeAgent(ua: string | null): string {
  if (!ua) return 'Navegador desconocido';
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Navegador';
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : null;
  return os ? `${browser} en ${os}` : browser;
}

/**
 * Sesiones simultáneas: pregunta al API por esta sesión cada pocos segundos (así sabe que alguien la tiene abierta) y,
 * si la persona ingresó también en otro equipo o navegador, muestra en ambas la pregunta de en cuál seguir. Mientras
 * tanto el API rechaza todo (409 `SESSION_CONFLICT`); la pregunta no se puede cerrar sin elegir. Lo que se elige en una
 * se ve en la otra en el siguiente sondeo: la que se cierra vuelve al ingreso con el motivo.
 */
export function SessionConflictWatcher({ onEnd }: { onEnd: (reason: SessionEndReason, endSessionUrl?: string | null) => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['session-state'],
    queryFn: async () => {
      try { return await apiGet<SessionState>('/auth/session'); } catch (e) {
        // Las rutas `/auth/` no avisan del 401 por su cuenta: la elección o la política la cerró desde la otra.
        if (e instanceof ApiError && e.status === 401) onEnd(END_CODES.has(e.code) ? (e.code as SessionEndReason) : 'SESSION_REVOKED');
        throw e;
      }
    },
    refetchInterval: (query) => (query.state.data?.conflict ? POLL_WAITING_MS : POLL_MS),
    refetchIntervalInBackground: true,
    retry: false,
  });
  const state = q.data;
  const conflict = state?.conflict ?? null;

  // Una petición cualquiera respondió que esta sesión está en espera: se pregunta ya, sin esperar el sondeo.
  useEffect(() => { onSessionConflict(() => { void q.refetch(); }); }, [q.refetch]);

  // Terminó la espera a favor de esta sesión: el aviso de por qué y lo que falló mientras tanto se vuelve a pedir.
  const wasWaiting = useRef(false);
  useEffect(() => {
    if (state?.notice) avisar.info(NOTICE[state.notice]);
    if (wasWaiting.current && !conflict) void qc.invalidateQueries({ predicate: (query) => query.queryKey[0] !== 'session-state' });
    wasWaiting.current = !!conflict;
  }, [state, conflict, qc]);

  const choose = useMutation({
    mutationFn: (keep: 'THIS' | 'OTHER') => apiPost<{ resolved: boolean; endSessionUrl: string | null }>('/auth/session/resolve', { keep }),
    onSuccess: (r, keep) => {
      if (keep === 'OTHER') return onEnd('SESSION_LEFT', r.endSessionUrl);
      wasWaiting.current = false;
      avisar.success('Continúas en esta sesión. La otra se cerró.');
      void qc.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 401) onEnd(END_CODES.has(e.code) ? (e.code as SessionEndReason) : 'SESSION_REVOKED');
      else avisar.error('No se pudo aplicar tu elección. Intenta de nuevo.');
    },
  });

  if (!conflict) return null;
  return <ConflictDialog conflict={conflict} busy={choose.isPending ? choose.variables : null} onChoose={(keep) => choose.mutate(keep)} onDeadline={() => void q.refetch()} />;
}

function ConflictDialog({ conflict, busy, onChoose, onDeadline }: {
  conflict: NonNullable<SessionState['conflict']>;
  busy: 'THIS' | 'OTHER' | null | undefined;
  onChoose: (keep: 'THIS' | 'OTHER') => void;
  onDeadline: () => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(t); }, []);
  const left = Math.max(0, Math.ceil((new Date(conflict.deadlineAt).getTime() - now) / 1000));
  // Al vencer, el API aplica la política en cuanto se le pregunta.
  useEffect(() => { if (left === 0) onDeadline(); }, [left === 0]);
  const timeout = conflict.policy === 'CLOSE_ALL'
    ? 'se cerrarán ambas sesiones'
    : conflict.newest ? 'seguirá solo esta sesión (la del ingreso más reciente)' : 'esta sesión se cerrará y seguirá la del ingreso más reciente';

  return (
    // Sin elegir no se cierra: ni con Escape ni fuera del diálogo.
    <AlertDialog open>
      {/* Más ancho que el de serie (`max-w-sm`): los dos botones, con su texto completo, caben en una fila. */}
      <AlertDialogContent className="data-[size=default]:sm:max-w-lg" onEscapeKeyDown={(e) => e.preventDefault()}>
        <AlertDialogHeader>
          <AlertDialogMedia><Icono nombre="mdi:account-multiple-check-outline" /></AlertDialogMedia>
          <AlertDialogTitle>Se ha detectado un nuevo inicio de sesión</AlertDialogTitle>
          <AlertDialogDescription>¿En cuál sesión deseas continuar? Mientras no elijas, ninguna de las dos puede trabajar en la plataforma.</AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="space-y-2 text-sm">
          <li className="flex items-start gap-2">
            <Icono nombre="mdi:monitor-account" className="mt-0.5 text-primary" aria-hidden />
            <span><strong>Esta sesión</strong>{conflict.newest ? ' · ingreso más reciente' : ''}</span>
          </li>
          {conflict.others.map((o, i) => <OtherSession key={i} session={o} />)}
        </ul>
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Si no eliges en <strong className="tnum text-foreground">{mmss(left)}</strong>, {timeout}.
        </p>
        <AlertDialogFooter>
          <Button variant="outline" disabled={!!busy} onClick={() => onChoose('OTHER')}>
            {busy === 'OTHER' ? <Spinner /> : <Icono nombre="mdi:logout" />} Continuar en la otra sesión
          </Button>
          <Button disabled={!!busy} onClick={() => onChoose('THIS')} autoFocus>
            {busy === 'THIS' ? <Spinner /> : <Icono nombre="mdi:check" />} Continuar en esta sesión
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function OtherSession({ session }: { session: ConflictingSession }) {
  return (
    <li className="flex items-start gap-2">
      <Icono nombre="mdi:monitor" className="mt-0.5 text-muted-foreground" aria-hidden />
      <span>
        <strong>Otra sesión</strong>: {describeAgent(session.userAgent)}
        <span className="text-muted-foreground">
          {session.ip ? ` · IP ${session.ip}` : ''} · ingresó a las <span className="tnum">{hhmm(session.createdAt)}</span>{session.newest ? ' (ingreso más reciente)' : ''}
        </span>
      </span>
    </li>
  );
}
